import { useMemo } from 'react';
import { isNil, orderBy } from 'lodash-es';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { getTotalNumberOfShares } from '../utils';
import { type ShareCertificate, type ShareCertificateRange, ShareCertificatesInner } from './ShareCertificatesInner';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { useShareCertificateTableDataQuery } from './ShareCertificatesTableData.graphql';

export const ShareCertificates = () => {
  const companyId = useCompanyId();

  const { data, loading, error } = useShareCertificateTableDataQuery({
    variables: { companyId },
    fetchPolicy: 'no-cache',
  });

  const combinedData: ShareCertificate[] = useMemo(() => {
    if (data?.shareholders && data?.company) {
      const ownerStates = data.shareState.ownerStates;

      let blockNumber = 1;
      orderBy(
        ownerStates.flatMap(os => os.ranges as ShareCertificateRange[]),
        r => r.start,
      ).forEach(r => (r.blockNumber = blockNumber++));

      const shareCertificates = ownerStates
        .map(os => {
          const sh = data.shareholders.find(sh => sh.id === os.shareholderId);
          if (isNil(sh)) {
            throw new Error('missing shareholder');
          }
          return {
            ownerName: sh.identity?.name,
            id: sh.id,
            shareholder: sh,
            totalNumberOfShares: getTotalNumberOfShares(os.ranges),
            blocks: os.ranges.length,
            blockNumber: blockNumber++,
            certificateIssued: os.ranges.some(r => r.shareCertificateDate) ? true : false,
            shareRanges: orderBy(os.ranges as ShareCertificateRange[], r => r.start),
          } satisfies ShareCertificate;
        })
        .filter(e => !isNil(e));
      return orderBy(shareCertificates, s => s.ownerName);
    }
    return [];
  }, [data?.company, data?.shareholders, data?.shareState]);

  if (loading || isNil(data)) {
    return <Skeleton />;
  }

  return <ShareCertificatesInner error={error} data={combinedData} />;
};
